import { format } from 'date-fns'
import { BellPlus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/formatters'
import { type OutboundCallAutomation, usePastAutomations } from '@/models/outboundCallAutomation'

function runSummary(run: OutboundCallAutomation): string {
  const c = run.leadCounts
  const parts = [
    `${c.total} total`,
    `${c.called} called`,
    `${c.skipped} skipped`,
    `${c.transferred} transferred`,
    `${c.noAnswer} no answer`,
  ]
  // Only when it happened — a healthy run shouldn't spend a column on a zero.
  if (c.failed > 0) parts.push(`${c.failed} dial failed`)
  return parts.join(' · ')
}

interface OutboundCallAutomationHistoryProps {
  excludeId?: string
  /**
   * Start a follow-up run from a past run (re-dial the leads it didn't reach).
   * Omitted while another automation is active — only one run at a time (INS-253).
   */
  onFollowUp?: (run: OutboundCallAutomation) => void
  /**
   * Schedule a reminder to run that follow-up later (INS-290). Unlike `onFollowUp`
   * this stays available while another automation is running — it only schedules.
   */
  onRemind?: (run: OutboundCallAutomation) => void
}

/** Log of past (completed) automation runs, so results stay reviewable later. */
export function OutboundCallAutomationHistory({
  excludeId,
  onFollowUp,
  onRemind,
}: OutboundCallAutomationHistoryProps) {
  const { data: past } = usePastAutomations()
  const runs = (past ?? []).filter((run) => run.id !== excludeId)
  if (!runs.length) return null

  return (
    <div className="flex flex-col gap-2 border-t pt-3">
      <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Past runs</div>
      {/* Two lines per run: name + date + actions, then the counts. One line can't
          hold all of it — the name was the first thing to get squeezed out. */}
      <ul className="flex max-h-52 flex-col gap-2.5 overflow-x-hidden overflow-y-auto pr-1">
        {runs.map((run) => (
          <li key={run.id} className="flex flex-col gap-0.5 text-sm">
            <div className="flex items-center gap-2">
              <span className="min-w-0 truncate font-medium">{run.name || 'Automation'}</span>
              <span className="text-muted-foreground shrink-0 text-xs">{formatDate(run.updatedAt)}</span>
              <span className="ml-auto flex shrink-0 items-center gap-2">
                {onFollowUp && run.followUpEligibleCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 shrink-0 text-xs"
                    onClick={() => onFollowUp(run)}
                  >
                    Follow-up ({run.followUpEligibleCount})
                  </Button>
                )}
                {onRemind && run.followUpEligibleCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 shrink-0 text-xs"
                    onClick={() => onRemind(run)}
                  >
                    <BellPlus className="mr-1 size-3.5" />
                    {run.followUpReminder
                      ? format(new Date(run.followUpReminder.reminderDate), 'MMM d, h:mm a')
                      : 'Remind me'}
                  </Button>
                )}
              </span>
            </div>
            <span className="text-muted-foreground text-xs">{runSummary(run)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
